import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconLink, IconTrash } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { notifications } from '@mantine/notifications';
import {
  useAttachContractTract,
  useContractTractsList,
  useDetachContractTract,
} from '../../queries/contracts';
import { useTractsList } from '../../queries/tracts';
import { ApiError } from '../../lib/api-error';
import type { ContractTract } from '../../types/api';

interface Props {
  contractId: string;
  canWrite: boolean;
}

export function ContractTractsPanel({ contractId, canWrite }: Props) {
  const { data: rows = [], isLoading } = useContractTractsList(contractId);
  const { data: allTracts = [] } = useTractsList({ limit: 500 });
  const attachM = useAttachContractTract();
  const detachM = useDetachContractTract();

  const [selectedTract, setSelectedTract] = useState<string | null>(null);
  const [lengthRods, setLengthRods] = useState<number | ''>('');
  const [lengthFeet, setLengthFeet] = useState<number | ''>('');
  const [widthFeet, setWidthFeet] = useState<number | ''>('');
  const [notes, setNotes] = useState('');

  const attachedIds = new Set(rows.map((r) => r.tract_id));
  const tractOptions = allTracts
    .filter((t) => !attachedIds.has(t.id))
    .map((t) => ({ value: t.id, label: `${t.name} — ${t.state}/${t.county}` }));

  async function onAttach() {
    if (!selectedTract) return;
    try {
      await attachM.mutateAsync({
        contract_id: contractId,
        tract_id: selectedTract,
        length_rods: lengthRods === '' ? null : Number(lengthRods),
        length_feet: lengthFeet === '' ? null : Number(lengthFeet),
        width_feet: widthFeet === '' ? null : Number(widthFeet),
        notes: notes.trim() || null,
        crossing_order: rows.length,
      });
      setSelectedTract(null);
      setLengthRods('');
      setLengthFeet('');
      setWidthFeet('');
      setNotes('');
      notifications.show({ title: 'Tract attached', message: '', color: 'pine' });
    } catch (err) {
      notifications.show({
        title: 'Attach failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  async function onDetach(row: ContractTract) {
    try {
      await detachM.mutateAsync(row.id);
      notifications.show({ title: 'Tract detached', message: row.tract_name ?? '', color: 'pine' });
    } catch (err) {
      notifications.show({
        title: 'Detach failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={4}>Tracts crossed</Title>
        <Badge variant="light" color="indigo">
          {rows.length} attached
        </Badge>
      </Group>

      {canWrite && (
        <Group align="flex-end" wrap="wrap">
          <Select
            label="Attach a tract"
            placeholder="Search tracts"
            data={tractOptions}
            value={selectedTract}
            onChange={setSelectedTract}
            searchable
            clearable
            w={300}
            disabled={tractOptions.length === 0}
          />
          <NumberInput
            label="Length (rods)"
            min={0}
            decimalScale={2}
            value={lengthRods}
            onChange={(v) => setLengthRods(v === '' ? '' : Number(v))}
            w={140}
          />
          <NumberInput
            label="Length (ft)"
            min={0}
            decimalScale={2}
            value={lengthFeet}
            onChange={(v) => setLengthFeet(v === '' ? '' : Number(v))}
            w={140}
          />
          <NumberInput
            label="Width (ft)"
            min={0}
            decimalScale={2}
            value={widthFeet}
            onChange={(v) => setWidthFeet(v === '' ? '' : Number(v))}
            w={120}
          />
          <TextInput
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            w={220}
          />
          <Button
            leftSection={<IconLink size={16} />}
            onClick={onAttach}
            disabled={!selectedTract}
            loading={attachM.isPending}
          >
            Attach
          </Button>
        </Group>
      )}

      <DataTable
        withTableBorder
        minHeight={120}
        fetching={isLoading}
        records={rows}
        idAccessor="id"
        noRecordsText="No tracts attached yet"
        columns={[
          {
            accessor: 'tract_name',
            title: 'Tract',
            render: (r) => (
              <Stack gap={0}>
                <Text size="sm" fw={500}>
                  {r.tract_name ?? r.tract_id}
                </Text>
                <Text size="xs" c="dimmed">
                  {r.tract_state}/{r.tract_county}
                </Text>
              </Stack>
            ),
          },
          { accessor: 'length_rods', title: 'Rods', textAlign: 'right' },
          { accessor: 'length_feet', title: 'Feet', textAlign: 'right' },
          { accessor: 'width_feet', title: 'Width (ft)', textAlign: 'right' },
          { accessor: 'notes', title: 'Notes', ellipsis: true },
          {
            accessor: 'actions',
            title: '',
            width: 50,
            render: (r) =>
              canWrite ? (
                <Tooltip label="Detach">
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="red"
                    onClick={() => onDetach(r)}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Tooltip>
              ) : null,
          },
        ]}
      />
    </Stack>
  );
}
